/**
 * Load-series proposal card (the runtime HITL gate) for
 * `propose_load_series`. Pure presentational; nothing happens until the
 * clinician presses Apply, and Apply only opens the DICOM import dialog with
 * the series selected — the clinician confirms the load there. Frame, status
 * chip and Apply/Reject live in {@link ProposalCardShell}.
 */
import { Typography } from '@mui/material';
import ProposalCardShell, {
    ProposalSummaryBox,
    type ProposalStatus,
} from './ProposalCardShell';

export interface LoadSeriesProposalCardProps {
    readonly patientId: string;
    readonly patientName: string;
    readonly targetName: string;
    readonly studyDescription: string;
    readonly studyDate: string;
    readonly series: ReadonlyArray<{
        readonly description: string;
        readonly modality: string;
        readonly sliceCount: number;
    }>;
    readonly status: ProposalStatus;
    readonly failureReason?: string;
    readonly onApply: () => void;
    readonly onReject: () => void;
    readonly onPreviewEnter: () => void;
    readonly onPreviewLeave: () => void;
}

export default function LoadSeriesProposalCard({
    patientId,
    patientName,
    targetName,
    studyDescription,
    studyDate,
    series,
    status,
    failureReason,
    onApply,
    onReject,
    onPreviewEnter,
    onPreviewLeave,
}: LoadSeriesProposalCardProps) {
    return (
        <ProposalCardShell
            title="Load from PACS"
            ariaLabel="Load series from PACS proposal"
            status={status}
            failureReason={failureReason}
            onApply={onApply}
            onReject={onReject}
            onPreviewEnter={onPreviewEnter}
            onPreviewLeave={onPreviewLeave}
        >
            <ProposalSummaryBox>
                <strong>{patientId}</strong>
                {patientName && patientName !== patientId
                    ? ` · ${patientName}`
                    : ''}
                <br />
                {studyDescription || 'Study'}
                {studyDate ? ` · ${studyDate}` : ''}
                {series.map((s, i) => (
                    <span key={i}>
                        <br />
                        {s.modality} {s.description || 'series'} ({s.sliceCount}{' '}
                        images)
                    </span>
                ))}
            </ProposalSummaryBox>
            <Typography variant="caption" color="text.secondary">
                Source: {targetName}. Apply opens the DICOM import dialog with
                these series selected; press Load there to import them.
            </Typography>
        </ProposalCardShell>
    );
}
